import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tag } from "@/components/Tag";
import { Link } from "react-router-dom";
import { Pencil } from "lucide-react";

function fmtMonth(ym) {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  const d = new Date(parseInt(y), parseInt(m) - 1, 1);
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

export function PromotionDetail({ open, onClose, promotion, onEdit }) {
  if (!promotion) return null;
  const headline = promotion.headline_belief;
  const supporting = promotion.supporting_beliefs || [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl bg-[#F2E9DB] border border-[#141B4D]/20 rounded-sm max-h-[90vh] overflow-y-auto"
        data-testid="promotion-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[#141B4D] text-2xl font-bold tracking-tight">
            {promotion.promotion_name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <span className="text-xs uppercase tracking-[0.1em] text-[#141B4D]/60">
            {fmtMonth(promotion.month)}
          </span>
          {promotion.offer_tag && <Tag>{promotion.offer_tag}</Tag>}
          <Tag
            variant={
              promotion.status === "Active"
                ? "active"
                : promotion.status === "Complete"
                  ? "retired"
                  : "default"
            }
          >
            {promotion.status}
          </Tag>
          {onEdit && (
            <button
              onClick={onEdit}
              data-testid="promotion-detail-edit-button"
              className="ml-auto inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border border-[#141B4D]/20 text-[#141B4D] hover:bg-[#141B4D]/5"
            >
              <Pencil size={12} /> Edit
            </button>
          )}
        </div>

        <section className="mb-6 pb-6 border-b border-[#141B4D]/15">
          <div className="text-[11px] uppercase tracking-[0.1em] text-[#141B4D]/60 mb-2">
            Headline belief
          </div>
          {headline ? (
            <Link
              to={`/beliefs/${headline.id}`}
              data-testid={`promotion-headline-belief-link-${headline.id}`}
              className="text-base text-[#141B4D] font-medium leading-relaxed hover:underline decoration-[#FFD100] decoration-2 underline-offset-4"
            >
              {headline.belief_text}
            </Link>
          ) : (
            <div className="text-sm text-[#141B4D]/50">No headline belief linked.</div>
          )}
        </section>

        <section className="mb-6 pb-6 border-b border-[#141B4D]/15">
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.1em] text-[#141B4D]/60">
              Supporting beliefs
            </div>
            <span className="text-xs text-[#141B4D]/55">
              {supporting.length} {supporting.length === 1 ? "belief" : "beliefs"}
            </span>
          </div>
          {supporting.length === 0 ? (
            <div className="text-sm text-[#141B4D]/50">None linked yet.</div>
          ) : (
            <ul className="space-y-3">
              {supporting.map((b) => (
                <li
                  key={b.id}
                  data-testid={`promotion-supporting-belief-${b.id}`}
                  className="bg-white border border-[#141B4D]/15 rounded-sm p-4"
                >
                  <Link
                    to={`/beliefs/${b.id}`}
                    className="text-sm text-[#141B4D] leading-relaxed hover:underline decoration-[#FFD100] decoration-2 underline-offset-4"
                  >
                    {b.belief_text}
                  </Link>
                  {b.category && (
                    <div className="text-xs text-[#141B4D]/55 mt-1.5">
                      {b.category}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.1em] text-[#141B4D]/60 mb-2">
              Call to action
            </div>
            <div
              data-testid="promotion-detail-cta"
              className="text-sm text-[#141B4D]"
            >
              {promotion.cta || "—"}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.1em] text-[#141B4D]/60 mb-2">
              Notes
            </div>
            <div
              data-testid="promotion-detail-notes"
              className="text-sm text-[#141B4D] whitespace-pre-wrap"
            >
              {promotion.notes || "—"}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
